import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Heading,
  Icon,
  Input,
  Progress,
  Select,
  Skeleton,
  Text,
  Textarea,
  Tooltip,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  MdAutoAwesome,
  MdLightbulbOutline,
  MdThumbUp,
} from "react-icons/md";
import {
  createManagerCoachingGoal,
  createManagerFeedback,
  getEmployeeCoachingPerformance,
  getManagerCoachingGoals,
  refreshEmployeeCoachingSummary,
  updateManagerCoachingGoal,
} from "../../services/managerEngagement";
import {
  comparisonPeriodDescription,
  reportingPeriodRange,
  stageDisplayLabel,
} from "./reportingPeriod";
import NarrativeCitation from "./NarrativeCitation";

function percentage(value) {
  return value == null ? "—" : `${Math.round(Number(value))}%`;
}

const SUMMARY_SECTIONS = [
  { key: "strengths", label: "Praise for", icon: MdThumbUp },
  { key: "opportunities", label: "Coach on", icon: MdLightbulbOutline },
];
const SUMMARY_POLL_INTERVAL_MS = 4000;
const SUMMARY_POLL_LIMIT = 15;

function trend(value) {
  if (value == null) return null;
  return `${value > 0 ? "+" : ""}${Number(value).toFixed(1)}`;
}

function PerformanceStat({ label, value, detail }) {
  return (
    <Box bg="white" borderWidth="1px" borderColor="#E7E8EC" borderRadius="2xl" p={5} boxShadow="0 8px 24px rgba(20, 31, 61, 0.05)">
      <Text color="#77819A" fontSize="xs" fontWeight="bold" letterSpacing="0.08em" textTransform="uppercase">{label}</Text>
      <Text mt={2} color="#111827" fontSize="3xl" lineHeight="1" fontWeight="bold">{value}</Text>
      {detail && <Text mt={2} color="#77819A" fontSize="sm">{detail}</Text>}
    </Box>
  );
}

export default function EmployeeCoachingPerformance({ employeeId, periodId, jobClass, frameworkFamily, reportingGroupId, locationId }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [goals, setGoals] = useState([]);
  const [goalTitle, setGoalTitle] = useState("");
  const [goalDimension, setGoalDimension] = useState("");
  const [goalTarget, setGoalTarget] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [summaryStatus, setSummaryStatus] = useState("idle");
  const summaryPollCountRef = useRef(0);
  const requestSequenceRef = useRef(0);

  const loadPerformance = useCallback(async ({ requestSummary = true, retryFailed = false } = {}) => {
    if (!jobClass) return;
    const requestSequence = ++requestSequenceRef.current;
    setError("");
    try {
      const response = await getEmployeeCoachingPerformance(employeeId, { periodId, jobClass, frameworkFamily, reportingGroupId, locationId });
      if (requestSequence !== requestSequenceRef.current) return;
      setData(response);
      const eligible = Boolean(
        frameworkFamily
        && Number(response.aggregate?.scorecard_count || 0) > 0
      );
      const baseStatus = !eligible
        ? "idle"
        : !response.narrative_summary
          ? "pending"
          : response.narrative_summary.stale
            ? "stale"
            : "ready";
      setSummaryStatus((current) => (
        !requestSummary
        && (current === "pending" || current === "updating")
        && (baseStatus === "pending" || baseStatus === "stale")
          ? current
          : baseStatus
      ));
      if (requestSummary && eligible && (!response.narrative_summary || response.narrative_summary.stale)) {
        const refresh = await refreshEmployeeCoachingSummary(employeeId, {
          periodId: response.period?.id || periodId,
          jobClass,
          frameworkFamily,
          reportingGroupId,
          locationId,
          retryFailed,
        });
        if (requestSequence !== requestSequenceRef.current) return;
        if (refresh.queued || refresh.reason === "in_progress") {
          setSummaryStatus(response.narrative_summary ? "updating" : "pending");
        } else if (refresh.reason === "previously_failed" && !response.narrative_summary) {
          setSummaryStatus("failed");
        } else if (response.narrative_summary) {
          setSummaryStatus("ready");
        }
      }
    } catch (err) {
      if (requestSequence === requestSequenceRef.current) {
        setError(err.response?.data?.detail || "Could not load coaching performance.");
      }
    }
  }, [employeeId, frameworkFamily, jobClass, locationId, periodId, reportingGroupId]);

  useEffect(() => {
    summaryPollCountRef.current = 0;
    loadPerformance();
    return () => { requestSequenceRef.current += 1; };
  }, [loadPerformance]);

  useEffect(() => {
    if (summaryStatus !== "pending" && summaryStatus !== "updating") return undefined;
    const timer = window.setInterval(() => {
      if (summaryPollCountRef.current >= SUMMARY_POLL_LIMIT) {
        setSummaryStatus("delayed");
        return;
      }
      summaryPollCountRef.current += 1;
      // The refresh endpoint is idempotent and is also the source of terminal
      // work status. Re-check it while polling so a failed summary does not
      // remain presented as "Preparing" until the local poll timeout.
      loadPerformance({ requestSummary: true });
    }, SUMMARY_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [loadPerformance, summaryStatus]);

  const retrySummary = () => {
    summaryPollCountRef.current = 0;
    setSummaryStatus(data?.narrative_summary ? "updating" : "pending");
    loadPerformance({ requestSummary: true, retryFailed: true });
  };

  useEffect(() => {
    let cancelled = false;
    getManagerCoachingGoals(employeeId, { reportingGroupId, locationId })
      .then((response) => { if (!cancelled) setGoals(response || []); })
      .catch(() => { if (!cancelled) setGoals([]); });
    return () => { cancelled = true; };
  }, [employeeId, locationId, reportingGroupId]);

  const threadUrl = (threadId, citation) => {
    const params = new URLSearchParams();
    if (periodId) params.set("period", periodId);
    if (jobClass) params.set("job_class", jobClass);
    if (frameworkFamily) params.set("framework", frameworkFamily);
    if (reportingGroupId) params.set("reporting_group", reportingGroupId);
    if (locationId) params.set("location", locationId);
    if (citation?.conversation_id) params.set("conversation", citation.conversation_id);
    if (citation?.evidence_id) params.set("evidence", citation.evidence_id);
    if (citation?.anchor?.detail_item_id != null) params.set("detail_item", citation.anchor.detail_item_id);
    if (citation?.anchor?.turn_index != null) params.set("turn", citation.anchor.turn_index);
    return `/manager/employees/${employeeId}/threads/${threadId}?${params.toString()}`;
  };

  const dimensions = data?.aggregate?.dimensions || [];
  const comparisonDescription = comparisonPeriodDescription(data?.period);
  const canCoach = data?.employee?.manager_access_level === "coach";
  const saveGoal = async () => {
    const dimension = dimensions.find((item) => `${item.dimension_type}:${item.dimension_id}` === goalDimension);
    if (!goalTitle.trim() || !dimension || !data?.job_class || !frameworkFamily) return;
    setSaving(true);
    try {
      const start = new Date();
      const due = new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000);
      const created = await createManagerCoachingGoal(employeeId, {
        title: goalTitle.trim(),
        job_class: data.job_class,
        framework_family: frameworkFamily,
        dimension_type: dimension.dimension_type,
        dimension_id: dimension.dimension_id,
        target_score: goalTarget === "" ? null : Number(goalTarget),
        start_at: start.toISOString(),
        due_at: due.toISOString(),
      }, { reportingGroupId, locationId });
      setGoals((current) => [created, ...current]);
      setGoalTitle(""); setGoalDimension(""); setGoalTarget("");
      toast({ status: "success", title: "Coaching goal created" });
    } catch (err) {
      toast({ status: "error", title: "Could not create goal", description: err.response?.data?.detail });
    } finally { setSaving(false); }
  };
  const sendFeedback = async () => {
    if (!feedbackMessage.trim()) return;
    setSaving(true);
    try {
      await createManagerFeedback(employeeId, feedbackMessage.trim());
      setFeedbackMessage("");
      toast({ status: "success", title: "Feedback shared" });
    } catch (err) {
      toast({ status: "error", title: "Could not share feedback", description: err.response?.data?.detail });
    } finally { setSaving(false); }
  };
  const completeGoal = async (goalId) => {
    setSaving(true);
    try {
      const updated = await updateManagerCoachingGoal(
        goalId,
        { status: "completed" },
        { reportingGroupId, locationId }
      );
      setGoals((current) => current.map((item) => item.id === updated.id ? updated : item));
      toast({ status: "success", title: "Coaching goal completed" });
    } catch (err) {
      toast({ status: "error", title: "Could not update goal", description: err.response?.data?.detail });
    } finally {
      setSaving(false);
    }
  };

  if (!jobClass) return null;
  if (error) return <Alert status="warning" borderRadius="lg"><AlertIcon />{error}</Alert>;
  if (!data) return <Skeleton h="180px" borderRadius="xl" />;
  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Text color="#3478E5" fontSize="xs" fontWeight="bold" letterSpacing="0.12em" textTransform="uppercase">Associate performance</Text>
        <Heading mt={1} size="lg" color="#101828">Coaching performance</Heading>
        <Text mt={1} color="#77819A" fontSize="sm">{reportingPeriodRange(data.period)}</Text>
      </Box>
      <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={3}>
        <PerformanceStat label="Performance score" value={percentage(data.aggregate.score)} detail="Across applicable coaching behaviors" />
        <Tooltip label={comparisonDescription} hasArrow>
          <Box><PerformanceStat label="Trend" value={trend(data.aggregate.trend) || "—"} detail="Score-point change from the previous period" /></Box>
        </Tooltip>
        <PerformanceStat label="Customer conversations" value={data.aggregate.scorecard_count} detail="Analyzed in this reporting period" />
      </Grid>
      <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={3}>
        {(data.aggregate.dimensions || []).filter((item) => item.dimension_type === "stage").map((dimension) => (
          <Box key={dimension.dimension_id} bg="white" borderWidth="1px" borderColor="#E7E8EC" borderRadius="xl" p={4}>
            <Flex justify="space-between" align="center" gap={3}>
              <Text color="#273149" fontWeight="semibold">{stageDisplayLabel(dimension.label)}</Text>
              <Text color="#3478E5" fontWeight="bold">{percentage(dimension.score)}</Text>
            </Flex>
            <Progress mt={3} value={dimension.score || 0} size="sm" colorScheme="blue" bg="#E9EEF7" borderRadius="full" />
            <Tooltip label={comparisonDescription} hasArrow>
              <Text mt={2} fontSize="xs" color={dimension.trend > 0 ? "green.600" : dimension.trend < 0 ? "red.500" : "gray.500"}>{dimension.trend == null ? "No prior-period comparison" : `${trend(dimension.trend)} pts versus prior period`}</Text>
            </Tooltip>
          </Box>
        ))}
      </Grid>
      {!data.narrative_summary && Number(data.aggregate?.scorecard_count || 0) > 0 && frameworkFamily && (
        <Alert status={summaryStatus === "failed" ? "warning" : "info"} borderRadius="xl">
          <AlertIcon />
          <Flex flex="1" justify="space-between" align="center" gap={3} wrap="wrap">
            <Text>
              {summaryStatus === "failed"
                ? "Associate Empowerment could not be prepared. Performance data remains available."
                : summaryStatus === "delayed"
                  ? "Associate Empowerment is taking longer than expected. Check again later."
                  : "Preparing evidence-grounded Associate Empowerment guidance."}
            </Text>
            {(summaryStatus === "failed" || summaryStatus === "delayed") && (
              <Button size="sm" onClick={retrySummary}>Check again</Button>
            )}
          </Flex>
        </Alert>
      )}
      {data.narrative_summary && (
        <Box bgGradient="linear(135deg, #367EEA 0%, #7654E8 100%)" color="white" borderRadius="2xl" p={{ base: 5, md: 7 }} boxShadow="0 14px 36px rgba(64, 93, 210, 0.22)">
          <Flex justify="space-between" align="center" gap={3} mb={5}>
            <HStack spacing={3}>
              <Flex boxSize="38px" borderRadius="xl" bg="whiteAlpha.200" align="center" justify="center"><Icon as={MdAutoAwesome} boxSize={5} /></Flex>
              <Box>
                <Heading size="md">Associate empowerment</Heading>
                <Text mt={1} color="whiteAlpha.800" fontSize="sm">Grounded guidance from this associate's coaching evidence.</Text>
              </Box>
            </HStack>
            {(summaryStatus === "updating" || summaryStatus === "stale") && <Badge bg="whiteAlpha.300" color="white">{summaryStatus === "stale" ? "Update delayed" : "Updating"}</Badge>}
          </Flex>
          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={5}>
            {SUMMARY_SECTIONS.map(({ label, key, icon }) => (
              <Box key={key} bg="rgba(255,255,255,0.12)" borderWidth="1px" borderColor="whiteAlpha.300" borderRadius="xl" p={4}>
                <HStack spacing={2}><Icon as={icon} /><Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em" textTransform="uppercase">{label}</Text></HStack>
                <VStack align="stretch" spacing={2} mt={2}>
                  {(data.narrative_summary.output[key] || []).slice(0, 3).map((item, index) => (
                    <Box key={`${key}-${index}`} bg="white" color="#273149" borderRadius="lg" p={3}>
                      <Text fontSize="sm" lineHeight="tall">{item.text}</Text>
                      {(item.citations || []).slice(0, 2).map((citation) => (
                        <NarrativeCitation
                          key={citation.evidence_id}
                          citation={citation}
                          onOpen={(value) => navigate(threadUrl(value.thread_id, value))}
                        />
                      ))}
                    </Box>
                  ))}
                  {!data.narrative_summary.output[key]?.length && <Text fontSize="sm" color="whiteAlpha.700">Not enough repeated evidence yet.</Text>}
                </VStack>
              </Box>
            ))}
          </Grid>
        </Box>
      )}
      <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={4}>
        <Box bg="white" borderWidth="1px" borderColor="#E7E8EC" borderRadius="2xl" p={5}>
          <Heading size="sm">Coaching goals</Heading>
          <VStack align="stretch" spacing={3} mt={3}>
            {goals.map((goal) => (
              <Box key={goal.id} borderTopWidth="1px" pt={3}>
                <Flex justify="space-between" gap={3}><Text fontWeight="semibold">{goal.title}</Text><Badge>{goal.status}</Badge></Flex>
                <Text mt={1} fontSize="sm" color="gray.500">{goal.dimension_label} · {percentage(goal.current_score)}{goal.target_score == null ? "" : ` / ${percentage(goal.target_score)}`}</Text>
                {goal.dimension_current === false && <Text mt={1} fontSize="xs" color="orange.600">This dimension is no longer in the current framework.</Text>}
                {canCoach && goal.status === "active" && <Button mt={2} size="xs" variant="ghost" isDisabled={saving} onClick={() => completeGoal(goal.id)}>Mark complete</Button>}
              </Box>
            ))}
            {!goals.length && <Text color="gray.500" fontSize="sm">No goals yet.</Text>}
            {canCoach && <Box borderTopWidth="1px" pt={3}><Input value={goalTitle} onChange={(event) => setGoalTitle(event.target.value)} placeholder="Goal title" aria-label="Goal title" /><Select mt={2} value={goalDimension} onChange={(event) => setGoalDimension(event.target.value)} aria-label="Goal dimension"><option value="">Select a coaching dimension</option>{dimensions.map((item) => <option key={`${item.dimension_type}:${item.dimension_id}`} value={`${item.dimension_type}:${item.dimension_id}`}>{item.label}</option>)}</Select><Input mt={2} type="number" min={0} max={100} value={goalTarget} onChange={(event) => setGoalTarget(event.target.value)} placeholder="Target score (optional)" aria-label="Goal target score" /><Button mt={3} size="sm" colorScheme="blue" isLoading={saving} onClick={saveGoal}>Add goal</Button></Box>}
          </VStack>
        </Box>
        <Box bg="white" borderWidth="1px" borderColor="#E7E8EC" borderRadius="2xl" p={5}>
          <Heading size="sm">Manager feedback</Heading>
          {canCoach ? <><Textarea mt={3} value={feedbackMessage} onChange={(event) => setFeedbackMessage(event.target.value)} placeholder="Share specific, actionable feedback" aria-label="Manager feedback" /><Button mt={3} size="sm" colorScheme="blue" isLoading={saving} onClick={sendFeedback}>Share feedback</Button></> : <Text mt={3} color="gray.500" fontSize="sm">Viewer access is read-only.</Text>}
        </Box>
      </Grid>
    </VStack>
  );
}
